<template>
  <base-slide-over ref="slideover" @hiding="reset()" title="Add Activity" size="lg">
    <base-form ref="form" v-model="isFormValid" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
      <base-select
        required
        label="Activity Type"
        :options="todoItemTypes"
        v-model="selectedItemTypeId"
        placeholder="Select activity type"
        label-key="label"
        value-key="todoItemTypeId"
      />
      <div class="tw-mb-4">
        <label :class="['tw-text-xs tw-font-normal', `tw-text-gray-900 tw-ml-3`]">Member</label>
        <member-search v-if="selectedMember === null" :businessId="businessId" @memberSelected="selectMember" />
        <div class="tw-text-sm tw-font-light tw-text-gray-700 tw-w-full tw-flex" v-else>
          <span class="tw-my-auto tw-ml-3">{{ selectedMember.fullName }}</span>
          <x-icon class="tw-h-5 tw-text-red-600 tw-cursor-pointer tw-ml-3" aria-label="Dismiss" @click="selectedMember = null" />
        </div>
      </div>

      <base-select
        label="Prescription"
        :disabled="isPrescriptionsLoading"
        :options="prescriptions"
        v-model="selectedPrescriptionId"
        placeholder="Select a prescription"
        label-key="name"
        value-key="id"
        class="tw-my-5"
      />

      <base-select
        :label="capitalize(this.organization.customerLabelSingular) + ' plan'"
        :disabled="isPlansLoading"
        :options="plans"
        v-model="selectedPlanId"
        placeholder="Select a plan"
        label-key="planName"
        value-key="userBusinessPlanId"
        class="tw-my-5"
      />

      <base-date-picker class="tw-mt-8" v-model="dueDate" :label="'Due Date'" :inline="true" required />

      <base-advanced-select
        v-if="showAssignUser"
        class="tw-mt-8"
        label="Assign to"
        :options="businessUsers"
        v-model="selectedUser"
        placeholder="Select a user"
        label-key="fullName"
        value-key="userId"
      />

      <base-textarea v-if="isNoteRequired" class="tw-mt-8" v-model="note" label="Note" />
    </base-form>

    <template #fixed-bottom>
      <inline-error class="tw-my-3" id="add-business-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
      <div class="sm:tw-flex sm:tw-flex-row-reverse tw-p-3">
        <base-button @click="save" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3" :disabled="!isFormValid">Save</base-button>
        <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">Cancel</base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import MemberSearch from "@/components/MemberSearch";
import InlineError from "@/components/InlineError";
import XIcon from "@/assets/svgs/x-icon.svg";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";
import moment from "moment";

export default {
  name: "NewTodoItem",
  components: { InlineError, MemberSearch, XIcon },
  props: {
    businessId: String,
    userId: { type: String, default: null },
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      selectedItemTypeId: null,
      errorTitle: "",
      errorMessage: "",
      error: null,
      todoItemTypes: [],
      selectedMember: null,
      dueDate: null,
      prescriptions: [],
      isPrescriptionsLoading: false,
      selectedPrescriptionId: null,
      plans: [],
      isPlansLoading: false,
      selectedPlanId: null,
      note: null,
      prefillUserId: null,
      prefillBusinessPrescriptionId: null,
      prefillUserBusinessPlanId: null,
      businessUsers: [],
      allUsers: null,
      selectedUser: null,
      businessLocationId: null,
      prefillTypeConstant: null,
    };
  },
  async created() {
    // If the component is created with a userId prop, load the user initially.
    // However, for pre-filling specific contexts (like clarification),
    // the `show` method will handle the main loading flow.
    await this.loadTodoTypes();
    await this.loadBusinessUsers();
    if (this.userId) {
      await this.loadUser(this.userId);
    }
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    computedSelectedType() {
      return this.todoItemTypes.find((i) => i.todoItemTypeId === this.selectedItemTypeId);
    },
    isNoteRequired() {
      const t = this.computedSelectedType;
      return t && (t.typeConstant === "PRESCRIBER_CLARIFICATION" || t.typeConstant === "ONLINE_SUPPORT_QUESTION");
    },
    showAssignUser() {
      return (
        this.allUsers &&
        Array.isArray(this.allUsers) &&
        this.allUsers.length > 0 &&
        (this.prefillTypeConstant === "PRESCRIBER_CLARIFICATION" && (this.computedSelectedType && this.computedSelectedType.typeConstant === "PRESCRIBER_CLARIFICATION")) &&
        (this.selectedPrescriptionId || this.selectedPlanId)
      );
    },
  },
  watch: {
    selectedPrescriptionId() {
      this.updateBusinessLocationIdAndUsers();
    },
    selectedPlanId() {
      this.updateBusinessLocationIdAndUsers();
    },
    userId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadUser(newVal);
      }
    },
    selectedMember: {
      handler(newVal) {
        if (newVal && newVal.userId) {
          this.loadPrescriptions();
          this.loadPlans();
        }
      },
      deep: true,
    },
  },
  methods: {
    capitalize,
    async show(prefill = {}) {
      this.$refs.slideover.show();
      this.reset();
      this.prefillUserId = prefill.userId;
      this.prefillBusinessPrescriptionId = prefill.businessPrescriptionId;
      this.prefillUserBusinessPlanId = prefill.userBusinessPlanId;

      this.isLoading = true;
      try {
        await this.loadTodoTypes();

        if (prefill.userId || prefill.businessPrescriptionId || prefill.userBusinessPlanId) {
          const prescriberClarificationType = this.todoItemTypes.find((type) => type.typeConstant === "PRESCRIBER_CLARIFICATION");
          if (prescriberClarificationType) {
            this.selectedItemTypeId = prescriberClarificationType.todoItemTypeId;
          }
          this.dueDate = moment()
            .startOf("day")
            .toDate();
        }

        let targetUserIdForLoad = this.prefillUserId || this.userId || null;
        if (targetUserIdForLoad) {
          await this.loadUser(targetUserIdForLoad);
        }

        if (this.prefillBusinessPrescriptionId && this.prescriptions.length > 0) {
          const foundRx = this.prescriptions.find((rx) => rx.id === this.prefillBusinessPrescriptionId);
          if (foundRx) this.selectedPrescriptionId = foundRx.id;
        }

        if (this.prefillUserBusinessPlanId && this.plans.length > 0) {
          const foundPlan = this.plans.find((plan) => plan.userBusinessPlanId === this.prefillUserBusinessPlanId);
          if (foundPlan) this.selectedPlanId = foundPlan.userBusinessPlanId;
        }

        this.businessLocationId = prefill.businessLocationId || null;
        this.prefillTypeConstant = prefill.prefillTypeConstant || null;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Error",
          message: "Failed to prefill activity form. Please try manually.",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async loadTodoTypes() {
      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/todo/types`);
        this.todoItemTypes = res.data.sort((a, b) => a.label.localeCompare(b.label));
      } catch {
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Error", message: "Failed to load todo types details for activity form.", type: "error" });
      }
    },
    async loadBusinessUsers() {
      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/users/all`);
        this.allUsers = Array.isArray(res.data) ? res.data : [];
        this.businessUsers = this.allUsers;
      } catch {
        this.allUsers = [];
        this.businessUsers = [];
      }
    },
    async loadUser(userIdToLoad) {
      if (!userIdToLoad) {
        this.selectedMember = null;
        this.prescriptions = [];
        this.plans = [];
        return;
      }
      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/members/${userIdToLoad}`);
        this.selectedMember = res.data;
        await this.loadPrescriptions();
        await this.loadPlans();
      } catch {
        this.selectedMember = null;
        this.prescriptions = [];
        this.plans = [];
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Error", message: "Failed to load member details for activity form.", type: "error" });
      }
    },
    async loadPrescriptions() {
      if (!this.selectedMember || !this.selectedMember.userId) {
        this.prescriptions = [];
        return;
      }
      this.isPrescriptionsLoading = true;
      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/members/${this.selectedMember.userId}/prescriptions`);
        this.prescriptions = Array.isArray(res.data) ? this.formatRxResults(res.data) : [];
      } catch {
        this.prescriptions = [];
      } finally {
        this.isPrescriptionsLoading = false;
      }
    },
    formatRxResults(items) {
      return Array.isArray(items)
        ? items.map((item) => ({
            id: item.id,
            name: item.businessProgram ? item.businessProgram.name : "Unknown Prescription",
            businessLocationId: item.businessLocationId || null,
          }))
        : [];
    },
    async loadPlans() {
      if (!this.selectedMember || !this.selectedMember.userId) {
        this.plans = [];
        return;
      }
      this.isPlansLoading = true;
      try {
        const res = await this.$axios.get(`/businesses/${this.businessId}/members/${this.selectedMember.userId}/memberplans`);
        this.plans = Array.isArray(res.data)
          ? res.data.map((plan) => ({
              ...plan,
              businessLocationId: plan.businessLocationId || null,
            }))
          : [];
      } catch {
        this.plans = [];
      } finally {
        this.isPlansLoading = false;
      }
    },
    selectMember(member) {
      this.selectedMember = member;
      this.loadPrescriptions();
      this.loadPlans();
    },
    reset() {
      this.dueDate = null;
      this.selectedItemTypeId = null;
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
      this.selectedPrescriptionId = null;
      this.selectedPlanId = null;
      this.note = null;
      this.prefillUserId = null;
      this.prefillBusinessPrescriptionId = null;
      this.prefillUserBusinessPlanId = null;
      this.selectedMember = null;
      this.prescriptions = [];
      this.plans = [];
      this.selectedUser = null;
      this.businessLocationId = null;
      this.prefillTypeConstant = null;
      this.loadBusinessUsers();
      if (this.$refs.form && typeof this.$refs.form.reset === "function") {
        this.$refs.form.reset();
      }
    },
    hide() {
      this.$refs.slideover.hide();
      this.reset();
    },
    save() {
      this.errorTitle = "";
      this.errorMessage = "";
      if (!this.$refs.form || typeof this.$refs.form.validate !== "function" || !this.$refs.form.validate()) return;

      if (this.selectedMember === null) {
        this.errorTitle = `Please select a ${this.organization.customerLabelSingular}`;
        this.errorMessage = `Please select a ${this.organization.customerLabelSingular} to assign this activity to.`;
        return;
      }
      if (this.selectedPrescriptionId === null && this.selectedPlanId === null) {
        this.errorTitle = `Please select a prescription or a plan`;
        this.errorMessage = `Activities must be associated with either a prescription or a plan. Please choose one.`;
        return;
      }
      if (
        this.computedSelectedType &&
        (this.computedSelectedType.typeConstant === "PRESCRIBER_CLARIFICATION" || this.computedSelectedType.typeConstant === "ONLINE_SUPPORT_QUESTION") &&
        (this.note === null || this.note.trim() === "")
      ) {
        this.errorTitle = `Please enter a note to the provider`;
        this.errorMessage = `This activity type requires a note to the provider. Please enter a note.`;
        return;
      }
      this.isLoading = true;

      this.$axios
        .post(`/businesses/${this.businessId}/todo`, {
          todoItemTypeId: this.selectedItemTypeId,
          userId: this.selectedMember.userId,
          dueDate: this.dueDate ? this.dueDate.toLocaleString("en-US") : null,
          businessPrescriptionId: this.selectedPrescriptionId,
          userBusinessPlanId: this.selectedPlanId,
          note: this.note,
          assignedToUserId: this.selectedUser ? this.selectedUser.userId : null,
        })
        .then(() => {
          this.EventBus.$emit("activitiesUpdated", {});
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Activity Saved", message: `Your activity was saved.` });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error creating activity`;
          this.errorMessage = "Sorry, we had trouble creating this activity. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    updateBusinessLocationIdAndUsers() {
      let locationId = null;

      if (this.selectedPrescriptionId) {
        const rx = Array.isArray(this.prescriptions) ? this.prescriptions.find((p) => p.id === this.selectedPrescriptionId) : null;
        if (rx && rx.businessLocationId) locationId = rx.businessLocationId;
      }
      if (!locationId && this.selectedPlanId) {
        const plan = Array.isArray(this.plans) ? this.plans.find((p) => p.userBusinessPlanId === this.selectedPlanId) : null;
        if (plan && plan.businessLocationId) locationId = plan.businessLocationId;
      }
      if (locationId && this.computedSelectedType && this.computedSelectedType.typeConstant === "PRESCRIBER_CLARIFICATION") {
        this.businessLocationId = locationId;
        this.filterBusinessUsers(locationId);
      } else {
        this.businessUsers = this.allUsers ? this.allUsers : [];
      }
    },
    filterBusinessUsers(businessLocationId) {
      if (!Array.isArray(this.allUsers)) {
        this.businessUsers = [];
        return;
      }
      const userOptions = [];
      userOptions.push({
        businessId: null,
        email: null,
        hasPin: false,
        locations: [],
        roles: [],
        userId: null,
        fullName: "-- Prescriber Location Users --",
        isHeader: true,
      });
      const locationUsers = this.allUsers
        .filter((user) => Array.isArray(user.locations) && user.locations.some((loc) => loc.businessLocationId === businessLocationId))
        .sort((a, b) => a.fullName.localeCompare(b.fullName));
      for (const user of locationUsers) {
        userOptions.push({
          businessId: user.businessId,
          email: user.email,
          fullName: user.fullName,
          hasPin: user.hasPin,
          locations: user.locations,
          roles: user.roles,
          userId: user.userId,
          isHeader: false,
        });
      }
      userOptions.push({
        businessId: null,
        email: null,
        hasPin: false,
        locations: [],
        roles: [],
        userId: null,
        fullName: "-- RxHarmony Users --",
        isHeader: true,
      });
      const rxhUsers = this.allUsers.filter((user) => !user.locations || user.locations.length === 0).sort((a, b) => a.fullName.localeCompare(b.fullName));
      for (const user of rxhUsers) {
        userOptions.push({
          businessId: user.businessId,
          email: user.email,
          fullName: user.fullName,
          hasPin: user.hasPin,
          locations: user.locations,
          roles: user.roles,
          userId: user.userId,
          isHeader: false,
        });
      }
      this.businessUsers = userOptions;
    },
  },
};
</script>
